import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { eatDayOf, startOfEatDay } from '@wodi/domain';
import { LAB_GATE_LABELS, type LabSampleView } from '@wodi/shared';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  printSheet,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useEffect, useRef, useState } from 'react';

import { collectLabSamples, labKeys, recordLabelPrinted, visitLabOrdersQuery } from '../api/lab.ts';
import { useOnline } from '../hooks/use-online.ts';
import { errorText, newRequestKey } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from './lab.module.css';
import { LabelRun } from './prints.tsx';

/** "14:05" today in East Africa Time, or null when the text isn't a time of day. */
export function collectedToday(text: string, now: Date): Date | null {
  const match = /^([01]?\d|2[0-3])[:.]([0-5]\d)$/.exec(text.trim());
  if (match === null) return null;
  const minutes = Number(match[1]) * 60 + Number(match[2]);
  return new Date(startOfEatDay(eatDayOf(now)).getTime() + minutes * 60_000);
}

/**
 * Prints the labels for samples just collected (or reprinted), one per sample times the
 * facility's copies, then records the print so reprints are audited (plan §8.3).
 */
export function usePrintLabels() {
  const runRef = useRef<HTMLDivElement>(null);
  const record = useMutation({
    mutationFn: async ({ samples, copies }: { samples: LabSampleView[]; copies: number }) => {
      for (const sample of samples) await recordLabelPrinted(sample.id, copies);
    },
  });
  const print = (samples: LabSampleView[], copies: number) => {
    if (runRef.current === null) return;
    printSheet(runRef.current);
    record.mutate({ samples, copies });
  };
  return { runRef, print, record };
}

/**
 * Collect (plan §8.3): the visit's collectable requests, ticked; one sample per specimen
 * and container is made on the server, each with a number from the facility's sequence;
 * the labels print straight away. Collection time is clinical time and defaults to now.
 */
export function CollectDialog({
  visitId,
  patientName,
  onClose,
}: {
  visitId: string;
  patientName: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const orders = useQuery(visitLabOrdersQuery(visitId));
  const [chosen, setChosen] = useState<Set<string> | null>(null);
  const [earlier, setEarlier] = useState('');
  const [tried, setTried] = useState(false);
  const requestKey = useRef(newRequestKey());
  const [openedAt] = useState(() => new Date());
  const { runRef, print, record } = usePrintLabels();

  const waiting = (orders.data ?? []).filter((o) => o.status === 'active' && o.sample === null);
  const ready = waiting.filter((o) => o.gate === 'ready');
  const selected = chosen ?? new Set(ready.map((o) => o.id));
  const collectedAt = earlier.trim() === '' ? null : collectedToday(earlier, openedAt);
  const timeError =
    earlier.trim() !== '' && (collectedAt === null || collectedAt.getTime() > openedAt.getTime())
      ? 'Enter a time earlier today, like 09:30.'
      : undefined;

  const collect = useMutation({
    mutationFn: () =>
      collectLabSamples(visitId, {
        clientRequestId: requestKey.current,
        orderIds: [...selected],
        ...(collectedAt === null ? {} : { collectedAt: collectedAt.toISOString() }),
      }),
    onSuccess: async (result) => {
      toasts.show({
        title: result.samples.length === 1 ? 'Sample collected' : 'Samples collected',
        description: result.samples.map((s) => s.sampleNumber).join(', '),
      });
      await queryClient.invalidateQueries({ queryKey: labKeys.work });
    },
  });
  const collected = collect.data;

  // The labels print once, as soon as the samples exist.
  const printed = useRef(false);
  useEffect(() => {
    if (collected === undefined || printed.current) return;
    printed.current = true;
    print(collected.samples, collected.labelCopies);
  }, [collected, print]);

  const go = () => {
    setTried(true);
    if (selected.size === 0 || timeError !== undefined || collected !== undefined) return;
    collect.mutate();
  };
  useShortcut('mod+enter', go, { allowInFields: true });

  return (
    <Dialog
      title={`Collect samples for ${patientName}`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {collected === undefined ? (
        <div className={styles.form}>
          {orders.isPending ? <LoadingBlock label="Loading the requests" rows={2} /> : null}
          {orders.isError ? (
            <Notice
              tone="error"
              action={
                <Button dense onPress={() => void orders.refetch()}>
                  Try again
                </Button>
              }
            >
              {errorText(orders.error, "This visit's lab requests couldn't be loaded.")}
            </Notice>
          ) : null}
          {orders.isSuccess && waiting.length === 0 ? (
            <Notice tone="info">Nothing left to collect for this visit.</Notice>
          ) : null}
          {waiting.map((order) => (
            <Checkbox
              key={order.id}
              isSelected={selected.has(order.id)}
              isDisabled={order.gate !== 'ready'}
              description={LAB_GATE_LABELS[order.gate]}
              onChange={(on) => {
                const next = new Set(selected);
                if (on) next.add(order.id);
                else next.delete(order.id);
                setChosen(next);
              }}
            >
              {order.testName}
            </Checkbox>
          ))}
          <p className={styles.meta}>
            Requests on the same specimen and container go into one sample, with one label.
          </p>
          <TextField
            label="Collected earlier today at"
            description="Leave empty if collected now."
            value={earlier}
            onChange={setEarlier}
            {...(timeError === undefined ? {} : { errorMessage: timeError })}
          />
          {tried && selected.size === 0 ? (
            <Notice tone="error">Tick at least one request to collect.</Notice>
          ) : null}
          {collect.isError ? (
            <Notice tone="error">
              {errorText(collect.error, "The samples weren't recorded. Try again.")}
            </Notice>
          ) : null}
        </div>
      ) : (
        <div className={styles.form}>
          <p>
            {collected.samples.length === 1
              ? 'The label is printing.'
              : `${String(collected.samples.length)} labels are printing.`}{' '}
            Stick each label on its tube before leaving the patient.
          </p>
          <div className={styles.preview}>
            <LabelRun samples={collected.samples} copies={collected.labelCopies} runRef={runRef} />
          </div>
          {record.isError ? (
            <Notice tone="error">
              {errorText(record.error, "The label print wasn't recorded. Reprint to record it.")}
            </Notice>
          ) : null}
        </div>
      )}
      <DialogActions>
        {collected === undefined ? (
          <>
            <Button variant="quiet" onPress={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={go}
              isPending={collect.isPending}
              pendingLabel="Collecting…"
              isDisabled={!online || waiting.length === 0}
            >
              Collect and print labels
            </Button>
          </>
        ) : (
          <>
            <Button
              onPress={() => {
                print(collected.samples, collected.labelCopies);
              }}
            >
              Reprint labels
            </Button>
            <Button variant="primary" onPress={onClose}>
              Done
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
